import { useState, useContext, useEffect } from "react";
import { Container, Table, Badge, Spinner, Form, InputGroup, Button } from "react-bootstrap";
import { FaSearch, FaCreditCard, FaMoneyBillWave, FaUser, FaRoute, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import AdminMasterLayout from '../../../layouts/Admin/AdminMasterLayout';
import "../../../assets/css/Admin/installments/AdminInstallments.css";

const AdminInstallments = () => {
  const { baseUrl } = useContext(AppContext);
  const authToken = localStorage.getItem("authToken");

  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchInstallments = () => {
    setLoading(true);
    axios
      .post(
        `${baseUrl}/admin/installment/viewAllInstallments`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data.success) {
          setInstallments(res.data.rides);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchInstallments();
  }, []);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
        return "admininstallments-badge admininstallments-badge--active";
      case "complete":
        return "admininstallments-badge admininstallments-badge--completed";
      case "overdue":
        return "admininstallments-badge admininstallments-badge--overdue";
      default:
        return "admininstallments-badge";
    }
  };

  const handleMarkPaid = (id) => {
    setActionLoadingId(id);
    axios
      .post(
        `${baseUrl}/admin/installment/markPaid`,
        { id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data.success) {
          setInstallments((prev) =>
            prev.map((item) =>
              item.id === id
                ? { ...item, inst_remaining: 0, inst_status: "complete" }
                : item
            )
          );
        }
        setActionLoadingId(null);
      })
      .catch((error) => {
        console.log(error);
        setActionLoadingId(null);
      });
  };

  const handleMarkOverdue = (id) => {
    setActionLoadingId(id);
    axios
      .post(
        `${baseUrl}/admin/installment/markOverdue`,
        { id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data.success) {
          setInstallments((prev) =>
            prev.map((item) =>
              item.id === id ? { ...item, inst_status: "overdue" } : item
            )
          );
        }
        setActionLoadingId(null);
      })
      .catch((error) => {
        console.log(error);
        setActionLoadingId(null);
      });
  };

  const filteredInstallments = installments.filter((item) => {
    const customerName = item.tdcustomer?.customerName || "";
    const matchesSearch = customerName
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || item.inst_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPlans = installments.length;
  const activePlans = installments.filter((i) => i.inst_status === "pending").length;
  const totalCollected = installments.reduce(
    (sum, i) => sum + (parseFloat(i.inst_paid) || 0),
    0
  );
  const totalPending = installments.reduce(
    (sum, i) => sum + (parseFloat(i.inst_remaining) || 0),
    0
  );

  return (
    <AdminMasterLayout screenName="customers">
      <div className="admininstallments-wrapper">
        <Container fluid className="admininstallments-container">
          <div className="admininstallments-header">
            <h2 className="admininstallments-title">
              <FaCreditCard className="me-2" />
              Installment Plans
            </h2>
            <p className="admininstallments-subtitle">
              All customer installment bookings and their payment status
            </p>
          </div>

          <div className="admininstallments-stats-row">
            <div className="admininstallments-stat-card">
              <div className="admininstallments-stat-icon admininstallments-stat-icon--total">
                <FaCreditCard />
              </div>
              <div>
                <p className="admininstallments-stat-label">Total Plans</p>
                <h4 className="admininstallments-stat-value">{totalPlans}</h4>
              </div>
            </div>
            <div className="admininstallments-stat-card">
              <div className="admininstallments-stat-icon admininstallments-stat-icon--active">
                <FaRoute />
              </div>
              <div>
                <p className="admininstallments-stat-label">Active Plans</p>
                <h4 className="admininstallments-stat-value">{activePlans}</h4>
              </div>
            </div>
            <div className="admininstallments-stat-card">
              <div className="admininstallments-stat-icon admininstallments-stat-icon--collected">
                <FaMoneyBillWave />
              </div>
              <div>
                <p className="admininstallments-stat-label">Collected</p>
                <h4 className="admininstallments-stat-value">
                  PKR {totalCollected.toLocaleString()}
                </h4>
              </div>
            </div>
            <div className="admininstallments-stat-card">
              <div className="admininstallments-stat-icon admininstallments-stat-icon--pending">
                <FaMoneyBillWave />
              </div>
              <div>
                <p className="admininstallments-stat-label">Pending</p>
                <h4 className="admininstallments-stat-value">
                  PKR {totalPending.toLocaleString()}
                </h4>
              </div>
            </div>
          </div>

          <div className="admininstallments-filters">
            <InputGroup className="admininstallments-search">
              <InputGroup.Text className="admininstallments-search-icon">
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by customer name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>

            <div className="admininstallments-status-tabs">
              {["all", "pending", "complete", "overdue"].map((s) => (
                <button
                  key={s}
                  className={`admininstallments-status-tab ${
                    statusFilter === s ? "admininstallments-status-tab--active" : ""
                  }`}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="admininstallments-table-card">
            {loading ? (
              <div className="admininstallments-loading">
                <Spinner animation="border" style={{ color: "#1B9B82" }} />
                <p>Loading installment plans...</p>
              </div>
            ) : filteredInstallments.length === 0 ? (
              <div className="admininstallments-empty">
                <FaCreditCard className="admininstallments-empty-icon" />
                <p>No installment plans found</p>
              </div>
            ) : (
              <div className="admininstallments-table-scroll">
                <Table responsive className="admininstallments-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Route</th>
                      <th>Ride Fare</th>
                      <th>Total Payable</th>
                      <th>Paid</th>
                      <th>Remaining</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInstallments.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="admininstallments-customer-cell">
                            <div className="admininstallments-avatar">
                              <FaUser />
                            </div>
                            <div>
                              <p className="admininstallments-customer-name">
                                {item.tdcustomer?.customerName || "N/A"}
                              </p>
                              <p className="admininstallments-customer-phone">
                                {item.tdcustomer?.customerPhone || ""}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <p className="admininstallments-route-cell">
                            {item.pickuplocation || "-"}
                            <span className="admininstallments-route-arrow">→</span>
                            {item.dropofflocation || "-"}
                          </p>
                        </td>
                        <td>PKR {parseFloat(item.rideFare || 0).toLocaleString()}</td>
                        <td className="admininstallments-total-text">
                          PKR {(parseFloat(item.inst_paid || 0) + parseFloat(item.inst_remaining || 0)).toLocaleString()}
                        </td>
                        <td className="admininstallments-paid-text">
                          PKR {parseFloat(item.inst_paid || 0).toLocaleString()}
                        </td>
                        <td className="admininstallments-remaining-text">
                          PKR {parseFloat(item.inst_remaining || 0).toLocaleString()}
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(item.inst_status)}>
                            {item.inst_status}
                          </span>
                        </td>
                        <td className="admininstallments-date-text">
                          {new Date(item.created_at).toLocaleDateString("en-GB")}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              disabled={item.inst_status === "complete" || actionLoadingId === item.id}
                              style={{ backgroundColor: "#1B9B82", border: "none" }}
                              onClick={() => handleMarkPaid(item.id)}
                            >
                              <FaCheckCircle className="me-1" />
                              Mark Paid
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={item.inst_status === "overdue" || actionLoadingId === item.id}
                              onClick={() => handleMarkOverdue(item.id)}
                            >
                              <FaExclamationTriangle className="me-1" />
                              Overdue
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </Container>
      </div>
    </AdminMasterLayout>
  );
};

export default AdminInstallments;